/**
 * @brisa/ui · facelift/bottom-nav-pill/BottomNavPill
 *
 * Feature: mobile-tweaks-v2 (Bruno 2026-05-19) · SPEC §3.2 + §6.4
 * Figma origem: node 218:1126 (Section Tag — Modelo Navegação Mobile)
 *
 * Pill flutuante de navegação mobile (md:hidden). Apresentacional puro —
 * recebe items + currentPath via props. No app, embrulhar com
 * BottomNavPillRouter (Client Component que chama usePathname).
 *
 * Tokens canônicos:
 *   bg rgba(255,255,255,0.24) + backdrop-blur 4px + rounded-40px (Bruno 2026-05-21)
 *   px-32 py-24 gap-32
 *   items: Archivo SemiBold 18px leading-none whitespace-nowrap
 *   active: text-accent-yellow (#FFE400) · inactive: text-foreground (black)
 */
import * as React from 'react';
import Link from 'next/link';

import { cn } from '@brisa/ui/lib/utils';

export interface BottomNavPillItem {
  href: string;
  label: string;
}

export interface BottomNavPillProps extends React.HTMLAttributes<HTMLElement> {
  /** Lista de items (default: Capa/Clubes/Planos). */
  items?: BottomNavPillItem[];
  /** Path atual — usado para destacar o item ativo. Comparação por startsWith. */
  currentPath?: string;
}

const DEFAULT_ITEMS: BottomNavPillItem[] = [
  { href: '/', label: 'Capa' },
  { href: '/clubes', label: 'Clubes' },
  { href: '/pass', label: 'Planos' },
];

function isItemActive(item: BottomNavPillItem, currentPath: string | undefined): boolean {
  if (!currentPath) return false;
  if (item.href === '/') return currentPath === '/';
  return currentPath === item.href || currentPath.startsWith(`${item.href}/`);
}

export function BottomNavPill({
  items = DEFAULT_ITEMS,
  currentPath,
  className,
  ...props
}: BottomNavPillProps) {
  return (
    <nav
      aria-label="Navegação principal mobile"
      data-slot="bottom-nav-pill"
      className={cn(
        'fixed bottom-6 left-1/2 z-40 -translate-x-1/2 md:hidden',
        'inline-flex items-center justify-center gap-8 rounded-[40px] px-8 py-6',
        'bg-[rgba(255,255,255,0.24)] backdrop-blur-[4px]',
        className,
      )}
      {...props}
    >
      {items.map((item) => {
        const active = isItemActive(item, currentPath);
        return (
          <Link
            key={item.href}
            href={item.href}
            aria-current={active ? 'page' : undefined}
            data-active={active ? 'true' : undefined}
            className={cn(
              'font-body text-[18px] font-semibold leading-none whitespace-nowrap transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-md',
              active
                ? 'text-[hsl(var(--accent-yellow))]'
                : 'text-[hsl(var(--text-100))] hover:text-[hsl(var(--text-80))]',
            )}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}
